import styled from '@emotion/styled/macro';
import React, { useState, useRef, useEffect, useCallback, Suspense, RefObject, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { Asset } from 'types';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import '@contently/videojs-annotation-comments/build/css/annotations.css';
import AnnotationComments from '@contently/videojs-annotation-comments';
import { useStoryContext, useRefetchStoryContext } from 'contexts/story';
import FloatingCommentForm from 'components/text-editor/common/FloatingCommentForm';
import TextEditorAnnotationComment from './TextEditorAnnotationComment';
import { CREATE_COMMENT } from '../../comments/queries';
import { useEditorContext } from 'contexts/editor';

/**
 * Container for this video asset to allow for the proper display of annotations
 */
const PositionContainer = styled.div`
  position: relative;
  line-height: normal;
  text-align: left;
`;

interface AssetVideoProps {
  asset: Asset;
  annotateButtonRef: RefObject<HTMLButtonElement>|null;
};


/**
 * Video Field component with support for video annotations.
 *
 * This component uses a VideoJS object {@link https://videojs.com/} augmented
 * with Contently's custom Videojs-Annotation-Comments plugin
 * {@see https://github.com/contently/videojs-annotation-comments} to display a
 * video file uploaded into a Video custom field in a story.
 */
export default function AssetVideo({ asset, annotateButtonRef }: AssetVideoProps) {
  const [showCommentForm, setShowCommentForm] = useState(false);
  const { activeCommentId, setActiveCommentId } = useEditorContext();
  const videoRef = useRef<HTMLDivElement>(null);
  const playerAndAnnotationsPositionContainerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<videojs.Player | null>(null);
  const [vjsAnnotationPlugin, setVjsAnnotationPlugin] = useState<any>(null);
  const story = useStoryContext();
  const refetchStoryContext = useRefetchStoryContext();
  const [createComment] = useMutation(CREATE_COMMENT);

  // Get the comments that apply to this video field asset only
  const commentsForThisAsset = useMemo(() => story.comments ? story.comments.filter( (comment: any) =>
    comment.assetId===parseInt(asset.id || "0") && !comment.resolved) : [], [story, asset]);

  const activeCommentBelongsToAsset = activeCommentId ? commentsForThisAsset.find((comment: any) => comment.id === activeCommentId) : null;

  /**
   * Callback for when the user clicks the "+💬" button.
   * Fires the addingAnnotation event on the annotation plugin and opens 
   * the New Annotation window
   */
  const handleCreateNewAnnotation = useCallback(() => {
    vjsAnnotationPlugin?.fire("addingAnnotation");
    setShowCommentForm(true);
    setActiveCommentId(null);
  }, [vjsAnnotationPlugin, setActiveCommentId]);

  /**
   * Callback for when the user clicks on one of the annotation markers
   * on the video.
   */
  const handleAnnotationMarkerClicked = useCallback( (event) => {
    setActiveCommentId(event.detail.annotation.id);
  }, [setActiveCommentId]);

  /**
   * Callback to handle saving an annotation.
   */
  const handleSubmitNewAnnotation = async ({body}: {body: string}) => {
    const startTime = Math.floor(playerRef?.current?.currentTime() || 0);
    const commentPayload = {
      commentableId: story.id,
      commentableType: 'Story',
      assetId: asset.id,
      startTime,
      body,
    };

    const createCommentResult = await createComment({variables: commentPayload});
    const newlyCreatedCommentId = createCommentResult.data.createComment.comment.id;
    await refetchStoryContext();

    // We need to create enough of an annotation object to make the VJS Annotation Plugin
    // show an annotation marker for the new comment.
    const newVideoAnnotation = {
      id: newlyCreatedCommentId,
      range: { start: startTime },
      comments:[]
    };
    vjsAnnotationPlugin.fire('newAnnotation', newVideoAnnotation);

    // Show the new annotation in an annotation popup
    setActiveCommentId(newlyCreatedCommentId);
    setShowCommentForm(false);
    vjsAnnotationPlugin.fire('cancelAddingAnnotation');
  };

  /**
   * Callback handler for when the user discards an unsaved comment.
   */
  const handleCancelComment = () => {
    setShowCommentForm(false);
    vjsAnnotationPlugin.fire('cancelAddingAnnotation');
  };

  /**
   * Callback handler for when the user clicks outside of the comment view popup
   */
  const handleHideComment = () => setActiveCommentId(null);

  /**
   * Get the comments on this story that correspond to this video field
   * in the appropriate format for the VideoJS Annotations Plugin
   * @returns {Array} Array of comment objects
   */
  const getAnnotationObjects = useCallback(() => {
    // Format them in the way the annotation plugin wants them
    return commentsForThisAsset.map( (comment: any) => ({
      id: comment.id,
      range: {
        start: comment.startTime,
      },
      comments: [{
        id: comment.id,
        meta: {
          datetime: comment.createdAt,
          user_name: comment.author.fullName,
        },
        body: comment.body,
      }],
    }));
  }, [commentsForThisAsset]);

  /**
   * Initialize the Video.JS library and annotation library plugin
   */
  const initializeVideoJs = useCallback( () => {
    if (!playerRef.current) {
      const videoJsOptions = {
        autoplay:                   false,
        controls:                   true,
        responsive:                 true,
        fluid:                      true,
        sources: [{
          src: asset.url,
          type: 'video/mp4',
        }],
      };

      const annotationPluginOptions = {
        annotationsObjects:         getAnnotationObjects(),
        showControls:               false,
        bindArrowKeys:              false,
        showCommentList:            false,
        showMarkerShapeAndTooltips: false,
        showFullScreen:             false,
        internalCommenting:         false,
        startInAnnotationMode:      true,
      };

      const videoElement = document.createElement("video-js");
      videoElement.classList.add('vjs-big-play-centered');
      videoRef.current?.appendChild(videoElement);

      // VideoJS plugin system doesn't play well with TypeScript unless you
      // cast player to 'any'.
      const player: any = playerRef.current = videojs(videoElement, videoJsOptions);
      if (!videojs.getPlugins().annotationComments) {
        videojs.registerPlugin('annotationComments', AnnotationComments(videojs));
      }
      const plugin = player.annotationComments(annotationPluginOptions);

      plugin.onReady( () => {
        plugin.registerListener('annotationOpened', handleAnnotationMarkerClicked);
      });
      setVjsAnnotationPlugin(plugin);
    }
  }, [getAnnotationObjects, handleAnnotationMarkerClicked, asset]);

  // On page load, set up the Video.js player object and annotations plugin
  useEffect(() => {
    initializeVideoJs();
    const annotateButtonCurrentRef = annotateButtonRef && annotateButtonRef.current ? annotateButtonRef.current : null;

    if (annotateButtonCurrentRef) {
      annotateButtonCurrentRef.addEventListener('click', handleCreateNewAnnotation, false);
    }
    // Cleanup the event listener on unmount
    return () => {
      annotateButtonCurrentRef?.removeEventListener('click', handleCreateNewAnnotation);
    };
  }, [videoRef, asset, initializeVideoJs, annotateButtonRef, handleCreateNewAnnotation]);

  // Cleanup function to displase of the Video.js player on unmount.
  useEffect( () => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  if (asset) {
    return (
      <PositionContainer ref={playerAndAnnotationsPositionContainerRef}>
        { showCommentForm && (
          <FloatingCommentForm
            yPos={"0"}
            handleSubmitComment={handleSubmitNewAnnotation}
            handleCancelComment={handleCancelComment}
          />
        ) }
        { activeCommentBelongsToAsset && (
          <Suspense fallback={null}>
            <TextEditorAnnotationComment
              activeAnnotation={activeCommentId || ''}
              yPos={"0"}
              hide={handleHideComment}
              storyId={story.id}
            />
          </Suspense>
        )}
        <div data-vjs-player>
          <div ref={videoRef} />
        </div>
      </PositionContainer>
    );
  }
  else {
    return null;
  }

}
